import {Injectable} from '@angular/core';
import {BulletinBoardPost} from '@app/core/models/bulletinBoardPost.model';
import {BulletinBoardPostComment} from '@app/core/models/bulletinBoardPostComment.model';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {IMessageBoardPostDataJson} from '@app/modules/home/modules/bulletin-board/components/create-edit-bulletin-post/create-edit-bulletin-post.component';
import {HttpService} from '@app/core/services/http.service';
import {InternationalizationService} from '@app/core/services/internationalization.service';
import {AuthService} from '@app/core/services/auth.service';
import {Store} from '@ngrx/store';
import {IAppState} from '@app/core/store/state/app.state';
import {BaseService} from '@app/core/services/base.service';
import {Pagination, PaginationHolder} from '@app/core/models/pagination.model';
import {UserService} from '@app/modules/shared/services/user.service';
import {BulletinBoardPostLike} from '@app/core/models/bulletinBoardPostLike.model';
import {IQOIconsOverlap} from '@quickorder/angular-web-lib/lib/types/interfaces/q-o-icons-overlap.interface';
import {Group} from '@app/core/models/group.model';
import {GroupService} from '@app/modules/shared/services/group.service';
import {User} from '@app/core/models/user.model';

export interface IBulletinBoardPostFilters {
	'filter[posted_in_department]'?: number;
}

export interface IBulletinBoardPostGroupsFilters {
	'filter[canPostMessageBoardPostsIn]'?: boolean;
	'include': string;
}

@Injectable({
	providedIn: 'root',
})
export class BulletinBoardService extends BaseService {

	constructor(
		public authService: AuthService,
		public interService: InternationalizationService,
		public httpService: HttpService,
		public store: Store<IAppState>,
		private userService: UserService,
		private groupService: GroupService,
	) {
		super(authService, interService, httpService, store);
	}

	public getBulletinBoardPosts(page_size: number, page: number, departmentId?: number): Observable<BulletinBoardPost[]> {
		let filters: IBulletinBoardPostFilters = {};
		if (departmentId) {
			filters = {
				'filter[posted_in_department]': departmentId,
			};
		}
		const data = {
			page_size,
			page,
			...filters,
		};

		return this.httpService.getJson<BulletinBoardPost[]>('messageBoardPosts', data).pipe(map((res: any) => {
			res.data = res.data.map((m: BulletinBoardPost) => {
				m.latest_message_board_post_comment = this.freshComment(m.latest_message_board_post_comment);
				m.overlappingLikeIcons = this.constructIcons(m.latest_four_message_board_post_likes);

				return this.fresh(m);
			});
			res.pagination = new Pagination().deserialize(res.pagination);

			return res;
		}));
	}

	public getBulletinBoardPost(id: number): Observable<BulletinBoardPost> {
		return this.httpService.getJson('messageBoardPosts/' + id).pipe(map((res: BulletinBoardPost) => {
			res.latest_message_board_post_comment = this.freshComment(res.latest_message_board_post_comment);

			return this.fresh(res);
		}));
	}

	public createPost(data: IMessageBoardPostDataJson): Observable<BulletinBoardPost> {
		return this.httpService.postJson<BulletinBoardPost>('messageBoardPosts', data).pipe(map(post => this.fresh(post)));
	}

	public patchPost(id: number, data: IMessageBoardPostDataJson): Observable<BulletinBoardPost> {
		return this.httpService.patchJson<BulletinBoardPost>('messageBoardPosts/' + id, data).pipe(map(post => {
			post.latest_message_board_post_comment = this.freshComment(post.latest_message_board_post_comment);
			post.overlappingLikeIcons = this.constructIcons(post.latest_four_message_board_post_likes);

			return this.fresh(post);
		}));
	}

	public deleteBulletinBoardPost(id: number): Observable<BulletinBoardPost> {
		return this.httpService.deleteJson<BulletinBoardPost>('messageBoardPosts/' + id);
	}

	public createComment(id: number, comment: string): Observable<BulletinBoardPostComment> {
		const data = {
			comment,
		};

		return this.httpService.postJson<BulletinBoardPostComment>('messageBoardPosts/' + id + '/comments', data).pipe(map((comm: any) => this.freshComment(comm)));
	}

	public patchComment(postId: number, commentId: number, comment: string): Observable<BulletinBoardPostComment> {
		const data = {
			comment,
		};

		return this.httpService.patchJson<BulletinBoardPostComment>('messageBoardPosts/' + postId + '/comments/' + commentId, data).pipe(map((comm: any) => this.freshComment(comm)));
	}

	public deleteComment(postId: number, commentId: number): Observable<BulletinBoardPostComment> {
		return this.httpService.deleteJson<BulletinBoardPostComment>('messageBoardPosts/' + postId + '/comments/' + commentId).pipe(map((comm: any) => this.freshComment(comm)));
	}

	public getBulletinBoardPostComments(postId: number, page_size: number, page: number): Observable<BulletinBoardPostComment[]> {
		const data = {
			page_size,
			page,
		};

		return this.httpService.getJson('messageBoardPosts/' + postId + '/comments', data).pipe(map((res: any) => {
			res.data = res.data.map(m => this.freshComment(m));
			res.pagination = new Pagination().deserialize(res.pagination);

			return res;
		}));
	}

	public likePost(post: BulletinBoardPost): Observable<BulletinBoardPostLike> {
		return this.httpService.postJson('messageBoardPosts/' + post.id + '/likes', {}).pipe(map((like: BulletinBoardPostLike) => this.freshLike(like)));
	}

	public dislikePost(post: BulletinBoardPost): Observable<BulletinBoardPostLike> {
		return this.httpService.deleteJson('messageBoardPosts/' + post.id + '/likes', {}).pipe(map((like: BulletinBoardPostLike) => this.freshLike(like)));
	}

	public getBulletinBoardPostGroups(filters: IBulletinBoardPostGroupsFilters): Observable<Group[]> {
		const data = {
			page_size: 200,
			...filters,
		};

		return this.httpService.getJson('groups', data).pipe(map((res: any) =>
			res.data.map(group => {
				return this.groupService.fresh(group);
			})));
	}

	public getPostSeenByInfo(post: BulletinBoardPost): Observable<User[]> {
		return this.httpService.getJson('messageBoardPosts/' + post.id + '/seenBy', {page: 1, page_size: 200}).pipe(map((res: User[]) =>
			res.map(user =>
				this.userService.fresh(user))));
	}

	public getPostLikedByInfo(post: BulletinBoardPost, page: number, page_size: number): Observable<PaginationHolder<BulletinBoardPostLike>> {
		return this.httpService.getJson<PaginationHolder<BulletinBoardPostLike>>('messageBoardPosts/' + post.id + '/likes', {page, page_size})
			.pipe(map((res: PaginationHolder<BulletinBoardPostLike>) => {
				res.data = res.data.map(like => this.freshLike(like));

				return res;
			}));
	}

	public getPunchBulletinBoardPosts(page_size: number, page: number): Observable<BulletinBoardPost[]> {
		const data = {
			page_size,
			page,
		};

		return this.httpService.getJson<BulletinBoardPost[]>('punchUser/messageBoardPosts', data).pipe(map((res: any) => {
			res.data = res.data.map((m: BulletinBoardPost) => {
				m.latest_message_board_post_comment = this.freshComment(m.latest_message_board_post_comment);
				m.overlappingLikeIcons = this.constructIcons(m.latest_four_message_board_post_likes);

				return this.fresh(m);
			});
			res.pagination = new Pagination().deserialize(res.pagination);

			return res;
		}));
	}

	public getPunchBulletinBoardPostComments(postId: number, page_size: number, page: number): Observable<BulletinBoardPostComment[]> {
		const data = {
			page_size,
			page,
		};

		return this.httpService.getJson('punchUser/messageBoardPosts/' + postId + '/comments', data).pipe(map((res: any) => {
			res.data = res.data.map(m => this.freshComment(m));
			res.pagination = new Pagination().deserialize(res.pagination);

			return res;
		}));
	}

	public getPunchPostLikedByInfo(post: BulletinBoardPost): Observable<PaginationHolder<BulletinBoardPostLike>> {
		return this.httpService.getJson('punchUser/messageBoardPosts/' + post.id + '/likes', {})
			.pipe(map((res: PaginationHolder<BulletinBoardPostLike>) => {
				res.data = res.data.map(like => this.freshLike(like));

				return res;
			}));
	}

	public getPunchPostSeenByInfo(post: BulletinBoardPost): Observable<User[]> {
		return this.httpService.getJson('punchUser/messageBoardPosts/' + post.id + '/seenBy', {page: 1, page_size: 200}).pipe(map((res: PaginationHolder<User>) =>
			res.data.map(user =>
				this.userService.fresh(user))));
	}

	private constructIcons(bulletinBoardPostLikes: BulletinBoardPostLike[]): IQOIconsOverlap[] {
		return bulletinBoardPostLikes.map(like => {
			like.user = this.userService.fresh(like.user);
			const icon = {imageUrl: undefined, initials: like.user.getInitials()};
			if (like.user.profile_picture && like.user.profile_picture.download_url) {
				icon['imageUrl'] = like.user.profile_picture.download_url;
			}

			return icon;
		});
	}

	private fresh(params?): BulletinBoardPost {
		if (!params) {
			return new BulletinBoardPost(this.httpService, true, this.tenantTZ, this.interService);
		}

		return new BulletinBoardPost(this.httpService, true, this.tenantTZ, this.interService).deserialize(params);
	}

	private freshComment(params): BulletinBoardPostComment {
		if (!params) {
			return new BulletinBoardPostComment(this.tenantTZ, this.interService);
		}

		return new BulletinBoardPostComment(this.tenantTZ, this.interService).deserialize(params);
	}

	private freshLike(params): BulletinBoardPostLike {
		if (!params) {
			return new BulletinBoardPostLike(this.tenantTZ, this.interService);
		}

		return new BulletinBoardPostLike(this.tenantTZ, this.interService).deserialize(params);
	}

}
